import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {MTAApi} from "components/src/api/admin/generated";
import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useEffect, useState} from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import {useNavigate} from "react-router-dom";

const mtaApi = new MTAApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface MTAViewEmailDialogProps {
    queueID : string
    onClose : OnClose
}

export const MTAViewEmailDialog = ({queueID, onClose} : MTAViewEmailDialogProps) => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The MIME content
     */
    const [mail, setMail] = useState<string>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while downloading the email to show a spinner on the download button
     */
    const [downloading, setDownloading] = useState<boolean>(false);
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        logger.debug("Load mail")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // get the email (but not more than 64Kib)
        mtaApi.mTAControllerGetMail(queueID, 64000)
            .then(response => {
                setMail(response.data as unknown as string);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting mail", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        queueID,
        setSpinnerStateCounter,
        t]);

    // called when the dialog is closed without clicking download
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () =>
    {
        // disable the Download component
        setDownload(false);
    }

    const downloadEmail = () =>
    {
        logger.debug("Download mail")

        setDownloading(true);

        mtaApi.mTAControllerGetMail(queueID)
            .then(response => {
                setBlob(new Blob([response.data as unknown as string], {type: "message/rfc822"}));

                // enable the Download component
                setDownload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error downloading mail", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setDownloading(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("mta.email-with-qid", {queuid: queueID})}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <InputGroup className="mb-3">
                    <Form.Control
                        className="font-monospace white-space-pre"
                        as="textarea"
                        defaultValue={mail??""}
                        rows={20}
                        autoFocus
                    />
                </InputGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" onClick={downloadEmail}>
                    {downloading && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {downloading ? t("common.downloading") : t("common.download")}
                </Button>
            </Modal.Footer>


            {download && blob !== undefined && (
                <Download blob={blob}
                          filename={`${queueID}.eml`}
                          onDownload={onDownload} />
            )}
        </Modal>
    );
}
